import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  TextField,
  Button,
  IconButton,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Autocomplete,
  Chip,
  Stack,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel,
  Popover,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import PrintIcon from '@mui/icons-material/Print';
import SendIcon from '@mui/icons-material/Send';
import PauseIcon from '@mui/icons-material/Pause';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SettingsIcon from '@mui/icons-material/Settings';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PaymentsIcon from '@mui/icons-material/Payments';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate, useLocation } from 'react-router-dom';
import PaymentSection from '../components/sales-invoice/PaymentSection';
import InvoiceDetailsSection from '../components/sales-invoice/InvoiceDetailsSection';
import CustomerInfoSection from '../components/sales-invoice/CustomerInfoSection';
import { useSnackbar } from 'notistack';
import { api, draftInvoiceService } from '../utils/apiService';
import InvoiceItemsTable from '../components/sales-invoice/InvoiceItemsTable';
import ValidationErrorDialog from '../components/common/ValidationErrorDialog';
import LoadingOverlay from '../components/common/LoadingOverlay';
import SuccessAnimation from '../components/common/SuccessAnimation';
import { useAuth } from '../context/AuthContext';
import settingsService from '../services/settingsService';

// Add these style constants at the top of the component, after the imports

const controlStyle = {
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    height: 36,
  },
  '& .MuiInputBase-input': {
    py: 0.75,
    fontSize: '0.875rem'
  },
  '& .MuiInputLabel-root': {
    fontSize: '0.875rem'
  },
  '& .MuiSelect-select': {
    py: 0.75
  }
};

const multilineStyle = {
  ...controlStyle,
  '& .MuiOutlinedInput-root': {
    borderRadius: 0,
    minHeight: 36,
  }
};

// Update calculateItemTotals function with bidirectional discount support
const calculateItemTotals = (item, updatedField = null) => {
  // Create a copy of the item to work with
  const updatedItem = { ...item };
  
  // Convert values to numbers and ensure they're valid
  updatedItem.quantity = Number(updatedItem.quantity) || 0;
  updatedItem.price = Number(updatedItem.price) || 0;
  updatedItem.discount = Number(updatedItem.discount) || 0;
  updatedItem.vatRate = Number(updatedItem.vatRate) || 0;
  updatedItem.discountAmount = Number(updatedItem.discountAmount) || 0;
  
  // Calculate line total (quantity * price)
  updatedItem.lineTotal = updatedItem.quantity * updatedItem.price;
  
  // Bidirectional Discount Calculation
  if (updatedField === 'discountAmount') {
    // User entered Discount Amount → Calculate Discount %
    if (updatedItem.lineTotal > 0) {
      updatedItem.discount = (updatedItem.discountAmount * 100) / updatedItem.lineTotal;
    } else {
      updatedItem.discount = 0;
    }
  } else {
    // User entered Discount % (or other field) → Calculate Discount Amount
    updatedItem.discountAmount = (updatedItem.lineTotal * updatedItem.discount) / 100;
  }
  
  // Calculate amount after discount
  updatedItem.afterDiscount = updatedItem.lineTotal - updatedItem.discountAmount;
  
  // Calculate VAT amount
  updatedItem.vatAmount = (updatedItem.afterDiscount * updatedItem.vatRate) / 100;
  
  // Calculate total including VAT
  updatedItem.total = updatedItem.afterDiscount + updatedItem.vatAmount;
  
  // Calculate price with VAT included
  updatedItem.priceWithVat = updatedItem.price * (1 + updatedItem.vatRate / 100);
  
  // Format numeric values for display
  updatedItem.lineTotal = parseFloat(updatedItem.lineTotal.toFixed(2));
  updatedItem.discount = parseFloat(updatedItem.discount.toFixed(2));
  updatedItem.discountAmount = parseFloat(updatedItem.discountAmount.toFixed(2));
  updatedItem.afterDiscount = parseFloat(updatedItem.afterDiscount.toFixed(2));
  updatedItem.vatAmount = parseFloat(updatedItem.vatAmount.toFixed(2));
  updatedItem.total = parseFloat(updatedItem.total.toFixed(2));
  updatedItem.price = parseFloat(updatedItem.price.toFixed(2));
  updatedItem.priceWithVat = parseFloat(updatedItem.priceWithVat.toFixed(2));

  return updatedItem;
};

function SalesInvoice() {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();
  const { currentTenant } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [error, setError] = useState(null);
  const [validationError, setValidationError] = useState(null);
  const [showValidationDialog, setShowValidationDialog] = useState(false);
  const [customerType, setCustomerType] = useState('B2C');
  const [customers, setCustomers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [tenantId, setTenantId] = useState(localStorage.getItem('tenantId'));
  const [shouldAutoAddItem, setShouldAutoAddItem] = useState(false);
  const [salesSettings, setSalesSettings] = useState({
    documentDiscountEnabled: true // Default to true
  });
  
  // Load sales settings on component mount
  useEffect(() => {
    const loadSalesSettings = async () => {
      try {
        const response = await settingsService.getCategory('sales');
        if (response.success && response.data) {
          setSalesSettings(response.data);
          
          // Re-fetch invoice number with new prefix if it's loaded
          if (response.data.invoicePrefix && invoice.invoiceNumber) {
            const numericPart = invoice.invoiceNumber.replace(/[^0-9]/g, '');
            const newInvoiceNumber = `${response.data.invoicePrefix}${numericPart}`;
            setInvoice(prev => ({ ...prev, invoiceNumber: newInvoiceNumber }));
          }
        }
      } catch (err) {
        console.error('Error loading sales settings:', err);
        // Keep default settings if loading fails
      }
    };
    
    loadSalesSettings();
  }, []);

  // Add useEffect to handle tenantId changes
  useEffect(() => {
    const storedTenantId = localStorage.getItem('tenantId');
    if (storedTenantId) {
      setTenantId(storedTenantId);
      setInvoice(prev => ({
        ...prev,
        tenantId: storedTenantId
      }));
    }
  }, []);
  
  // Initialize fromDraft and draftInvoiceId based on location state
  const isFromDraft = Boolean(location.state?.fromDraft);
  const [fromDraft, setFromDraft] = useState(isFromDraft);
  const [draftInvoiceId, setDraftInvoiceId] = useState(location.state?.draftInvoiceId || null);
  
  // Use refs to track loading states and prevent infinite loops
  const draftDataLoadedRef = React.useRef(false);
  const productsLoadedRef = React.useRef(false);
  const customersLoadedRef = React.useRef(false);
  const draftFetchedRef = React.useRef(false);
  
  // Store the draft invoice data in a ref to avoid dependency issues
  const draftDataRef = React.useRef({
    draftInvoice: null,
    draftItems: [],
    draftPayments: []
  });
  // Create a ref for the product selection field in the data grid
  const productFieldRef = React.useRef(null);

  // Set initial focus on product field
  useEffect(() => {
    const timer = setTimeout(() => {
      const productField = document.querySelector('[data-new-item-field="product"]');
      if (productField) {
        productField.focus();
      }
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);
  
  const [invoice, setInvoice] = useState({
    invoiceNumber: '',
    date: new Date(),
    dueDate: new Date(),
    customer: null,
    agent: null,
    items: [],
    notes: '',
    terms: 'Payment due within 30 days. Late payments subject to 2% monthly interest.',
    status: 'New',
    paymentStatus: 'Pending',
    paymentAmount: '',
    paymentReference: '',
    payments: [],
    additionalCost: '0',
    discountPercentage: '0',
    discountAmount: '0',
    tenantId: localStorage.getItem('tenantId'), // Add tenantId from localStorage
    quotationReference: null // Reference to quotation if converted from quotation
  });

  // Prepare master data for invoice submission
  const prepareMasterData = (invoiceData, customerTypeValue) => {
    const totalCalculation = calculateTotal(invoiceData.items);
    const totalAmount = totalCalculation.total; // Extract the total number from the object
    const paidAmount = getTotalPayments() || 0;
    const balanceAmount = Math.max(0, totalAmount - paidAmount);
    
    // Safe access to customer data
    const customer = invoiceData.customer || {};
    const address = customer.address || {};
    
    return {
      status: (invoiceData.status || 'hold').toLowerCase(),
      invoiceDate: invoiceData.date || new Date(),
      dueDate: invoiceData.dueDate || new Date(),
      paymentStatus: (invoiceData.paymentStatus || 'pending').toLowerCase(),
      customerType: customerTypeValue,
      customerCode: customer.code || (customer.name === 'Walk In Customer' ? 'WLKINCSTMR-119' : ''),
      customerName: customer.name || 'Walk In Customer',
      customerAddress: customer.name === 'Walk In Customer' ? 'Walk In Customer' : (address.street || ''),
      customerPhone: customer.phone || '',
      customerStreetName: address.city || '',
      customerPostalCode: address.postalCode || '',
      agentId: invoiceData.agent?._id || '',
      subTotal: calculateSubtotal(invoiceData.items),
      additionalCost: parseFloat(invoiceData.additionalCost) || 0,
      discountPerc: parseFloat(invoiceData.discountPercentage) || 0,
      discountAmt: parseFloat(invoiceData.discountAmount) || 0,
      vatAmt: calculateVat(invoiceData.items),
      totalAmt: totalAmount,
      paidAmt: paidAmount,
      balanceAmt: balanceAmount,
      notes: invoiceData.notes || '',
      tenantId: invoiceData.tenantId || ''
    };
  };

  // Prepare details data for invoice submission
  const prepareDetailsData = (invoiceData) => {
    return invoiceData.items.map(item => {
      // Ensure itemId is always set
      if (!item.product || !item.product.id) {
        throw new Error('Product ID is required for all invoice items');
      }
      
      return {
        itemId: item.product.id, // Use the product.id field which is mapped from _id in the fetchProducts function
        itemCode: item.product?.productCode || '',
        qty: item.quantity,
        unitPrice: item.price,
        priceWithVat: item.priceWithVat,
        totalAmt: item.total,
        discountPerc: item.discountPerc || 0,
        discountAmt: item.discountAmt || 0,
        netValue: item.netValue,
        vat: item.vatRate,
        vatAmt: item.vatAmt,
        grandTotal: item.grandTotal,
        description: item.description || '',
        tenantId: invoiceData.tenantId
      };
    });
  };

  // Validate invoice data before submission
  const validateInvoice = (invoiceData) => {
    if (!invoiceData.customer) {
      throw new Error('Please select a customer');
    }

    if (invoiceData.items.length === 0) {
      throw new Error('Please add at least one item to the invoice');
    }
    
    // Validate that all items have a product selected with a valid ID
    const invalidItems = invoiceData.items.filter(item => !item.product || !item.product.id);
    if (invalidItems.length > 0) {
      throw new Error('All invoice items must have a product selected with a valid ID');
    }

    // Validate unit prices
    const invalidUnitPrices = invoiceData.items.filter(item => {
      const unitPrice = parseFloat(item.price);
      return isNaN(unitPrice) || unitPrice <= 0;
    });
    
    if (invalidUnitPrices.length > 0) {
      const invalidItemNumbers = invalidUnitPrices.map((_, index) => index + 1).join(', ');
      throw new Error(`Invalid unit price detected in item(s) #${invalidItemNumbers}. All items must have a unit price greater than 0.`);
    }
  };

  // Consolidate fetchNextInvoiceNumber into a single function
  const fetchNextInvoiceNumber = async () => {
    try {
      const response = await api.get('/sales/next-invoice-number');
      if (response.data.success) {
        // Backend already applies the prefix from settings, so just use it directly
        const nextNumber = response.data.data.nextInvoiceNumber;
        
        console.log('Fetched next invoice number from backend:', nextNumber);
        
        setInvoice(prev => ({ ...prev, invoiceNumber: nextNumber }));
        return nextNumber;
      }
      throw new Error('Failed to fetch invoice number');
    } catch (error) {
      console.error('Error fetching next invoice number:', error);
      throw error;
    }
  };

  // Single useEffect for initial invoice number fetch
  useEffect(() => {
    const fetchInitialInvoiceNumber = async () => {
      try {
        await fetchNextInvoiceNumber();
      } catch (error) {
        enqueueSnackbar('Error fetching invoice number', { variant: 'error' });
      }
    };
    fetchInitialInvoiceNumber();
  }, [enqueueSnackbar]);

  // Reset the invoice form after successful submission
  const resetInvoiceForm = async (tenantId) => {
    // Add a delay to ensure the database has been updated with the new invoice
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    const nextInvoiceNumber = await fetchNextInvoiceNumber();
    
    setInvoice({
      invoiceNumber: nextInvoiceNumber,
      date: new Date(),
      dueDate: new Date(),
      customer: null,
      agent: null,
      items: [],
      notes: '',
      terms: 'Payment due within 30 days. Late payments subject to 2% monthly interest.',
      status: 'New',
      paymentStatus: 'Pending',
      paymentAmount: '',
      paymentReference: '',
      payments: [],
      additionalCost: '0',
      discountPercentage: '0',
      discountAmount: '0',
      tenantId: tenantId
    });
    
    setNewItem({
      product: null,
      description: '',
      quantity: 1,
      price: 0,
      vatRate: 15,
      discount: 0,
      unit: '',
    });
  };

  // Function to submit invoice to backend
  const submitInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      setValidationError(null);
      
      // Validate invoice data
      try {
        validateInvoice(invoice);
      } catch (err) {
        setValidationError(err.message);
        setShowValidationDialog(true);
        setLoading(false);
        return;
      }
      
      // Calculate payment status based on payments
      const totalAmount = calculateTotal(invoice.items);
      const paidAmount = getTotalPayments() || 0;
      const paymentStatus = paidAmount >= totalAmount ? 'paid' : 'pending';
      
      // Prepare data for API
      const masterData = prepareMasterData(invoice, customerType);
      const detailsData = prepareDetailsData(invoice);
      
      // Add payment data if any payments exist
      const paymentData = invoice.payments.map(payment => ({
        method: payment.methodId || payment.method, // Use methodId if available, fallback to method
        amount: payment.amount,
        reference: payment.reference || '',
        date: payment.date
      }));
      
      // Create request body
      const requestBody = {
        master: masterData,
        details: detailsData,
        payments: paymentData
      };
      
      // Submit to backend
      const response = await api.post('/sales', requestBody);

      if (response.data.success) {
        // If this was converted from a draft, delete the original draft invoice
        if (fromDraft && draftInvoiceId) {
          try {
            // Make sure we're using the correct invoice number format
            // It could be either the direct draftInvoiceId or from the draft invoice data
            const invoiceNoToDelete = draftInvoiceId || 
                                     draftDataRef.current?.draftInvoice?.invoiceNo || 
                                     draftInvoiceId;
            
            // Add a small delay to ensure the backend has completed processing the new invoice
            await new Promise(resolve => setTimeout(resolve, 500));
            
            // Delete the draft invoice using the correct API endpoint
            const deleteResponse = await api.delete(`/draft-invoices/${invoiceNoToDelete}`);
            
            if (deleteResponse.data && deleteResponse.data.success) {
              enqueueSnackbar('Draft invoice deleted successfully', { variant: 'success' });
            }
          } catch (deleteError) {
            console.error('Error deleting draft invoice after conversion:', deleteError);
            enqueueSnackbar('Invoice created, but failed to delete draft invoice', { variant: 'warning' });
            // We don't want to fail the whole process if just the draft deletion fails
          }
        }

        // If this was converted from a quotation, mark the quotation as converted
        if (invoice.quotationReference) {
          try {
            await api.patch(`/quotations/${invoice.quotationReference}/mark-converted`, {
              invoiceNo: response.data.data?.invoiceNo || invoice.invoiceNumber
            });
          } catch (quotationError) {
            console.error('Error marking quotation as converted:', quotationError);
            // Don't fail the whole process if marking fails
          }
        }
        
        enqueueSnackbar('Invoice created successfully', { variant: 'success' });
        
        // Show success animation
        setLoading(false);
        setShowSuccessAnimation(true);
        
        // Reset form after animation completes
        setTimeout(async () => {
          await resetInvoiceForm(invoice.tenantId);
        }, 2500);
      } else {
        throw new Error(response.data.message || 'Failed to create invoice');
      }
    } catch (error) {
      console.error('Error submitting invoice:', error);
      setError(error.message || 'An error occurred while creating the invoice');
      enqueueSnackbar(
        error.response?.data?.message || error.message || 'Failed to create invoice', 
        { variant: 'error' }
      );
    } finally {
      setLoading(false);
    }
  };

  const [newItem, setNewItem] = useState({
    product: null,
    description: '',
    quantity: 0,
    price: 0,
    vatRate: 15,
    discount: 0,
    unit: '',
  });

  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const [visibleColumns, setVisibleColumns] = useState({
    description: false,
    discountPerc: false,
    discountAmt: false,
    netValue: false,
    priceWithVat: true,
    vatAmount: true,
    grandTotal: true
  });
  
  const settingsOpen = Boolean(settingsAnchorEl);

  const [termsExpanded, setTermsExpanded] = useState(false);
  const [paymentExpanded, setPaymentExpanded] = useState(true);

  const [openAgentDialog, setOpenAgentDialog] = useState(false);
  const [agentDialogMode, setAgentDialogMode] = useState('add'); // 'add' or 'edit'
  const [currentAgent, setCurrentAgent] = useState(null);
  const [newAgent, setNewAgent] = useState({ name: '' });
  const [customAgents, setCustomAgents] = useState([]);

  const productInputRef = useRef(null);

  // Effect to set initial focus (only on mount)
  useEffect(() => {
    // Short delay to ensure component is fully mounted
    const timer = setTimeout(() => {
      if (productInputRef.current && !newItem.product) {
        productInputRef.current.focus();
      }
    }, 100);
    return () => clearTimeout(timer);
  }, []); // Run only on mount, not when product changes

  const handleProductInputRef = (ref) => {
    // Store the ref for later use
    productInputRef.current = ref;
  };

  const handleFocus = (element) => {
    if (element?.target) {
      element.target.select();
    } else if (element?.select) {
      element.select();
    }
  };
  
  // Check for draft invoice data from navigation
  useEffect(() => {
    // Only process location state once to prevent infinite loops
    if (!draftDataLoadedRef.current && location.state && location.state.fromDraft) {
      setFromDraft(true);
      
      // Handle the case where we have draftInvoiceId directly from Sales.js
      if (location.state.draftInvoiceId) {
        setDraftInvoiceId(location.state.draftInvoiceId);
      }
      // Handle the legacy case where we have the full invoice object
      else if (location.state.draftInvoice) {
        setDraftInvoiceId(location.state.draftInvoice.invoiceNo);
      }
    } else if (!location.state || !location.state.fromDraft) {
      // Reset the ref if we're not coming from a draft
      draftDataLoadedRef.current = false;
    }
  }, [location]);
  
  // State for products data
  const [products, setProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);

  // Fetch active products from API - only run once when component mounts
  useEffect(() => {
    const fetchProducts = async () => {
      // Skip if already loaded
      if (productsLoadedRef.current) {
        return;
      }
      
      setProductsLoading(true);
      try {
        const response = await api.get('/products/active');
        if (response.data.success) {
          // Transform the product data to match the expected format
          const formattedProducts = response.data.data.map(product => ({
            id: product._id,
            name: product.productName,
            description: product.description || '',
            price: product.salesRate || 0,
            altSalesRate: product.altSalesRate || 0,
            vat: product.taxRate !== undefined && product.taxRate !== null ? product.taxRate : 15,
            productCode: product.productCode,
            basicUnit: product.basicUnit || '',
            alternateUnit: product.alternateUnit || '',
            // Include any other fields needed for the invoice
          }));
          
          setProducts(formattedProducts);
          productsLoadedRef.current = true;
          
          // If we have draft data already fetched and products are loaded, we can load it into the form
          if (fromDraft && customers.length > 0 && !draftDataLoadedRef.current && draftDataRef.current.draftInvoice) {
            loadDraftInvoiceData();
          }
        } else {
          enqueueSnackbar('Failed to load products', { variant: 'error' });
        }
      } catch (error) {
        console.error('Error fetching products:', error);
        enqueueSnackbar('Error loading products: ' + (error.response?.data?.message || error.message), { variant: 'error' });
      } finally {
        setProductsLoading(false);
      }
    };

    fetchProducts();
    
    // Empty dependency array means this only runs once when component mounts
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch draft invoice data if needed - runs when fromDraft or draftInvoiceId changes
  useEffect(() => {
    if (!fromDraft || !draftInvoiceId || draftFetchedRef.current) return;

    const fetchDraftInvoiceData = async () => {
      setLoading(true);
      draftFetchedRef.current = true;
      try {
        const response = await api.get(`/draft-invoices/${draftInvoiceId}`);
        if (response.data.success) {
          draftDataRef.current = {
            draftInvoice: response.data.data,
            draftItems: response.data.items || [],
            draftPayments: response.data.payments || [],
          };
          if (customers.length > 0 && products.length > 0) {
            loadDraftInvoiceData();
          }
        }
      } catch (error) {
        console.error('Error loading draft invoice:', error);
        enqueueSnackbar('Error loading draft invoice', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchDraftInvoiceData();
  }, [fromDraft, draftInvoiceId, customers.length, products.length, enqueueSnackbar]);

  // Function to load draft invoice data into the form
  const loadDraftInvoiceData = () => {
    if (!draftDataRef.current) return;

    const { draftInvoice, draftItems, draftPayments } = draftDataRef.current;
    
    // Calculate correct payment status based on payments
    const totalAmount = draftItems.length > 0 ? calculateTotal(draftItems).total : 0;
    const totalPaid = draftPayments.reduce((sum, payment) => sum + (payment.amount || 0), 0);
    
    let correctPaymentStatus = 'Pending';
    if (totalPaid >= totalAmount && totalAmount > 0) {
      correctPaymentStatus = 'Paid';
    } else if (totalPaid > 0) {
      correctPaymentStatus = 'Partially Paid';
    }
    
    // Load draft data into the form with recalculated payment status
    setInvoice(prev => ({
      ...prev,
      ...draftInvoice,
      items: draftItems,
      payments: draftPayments,
      paymentStatus: correctPaymentStatus  // Override with correct status
    }));

    // Clear the ref after loading
    draftDataRef.current = null;
  };

  // Update useEffect to fetch all customers - only run once when component mounts
  useEffect(() => {
    const fetchCustomers = async () => {
      // Skip if already loaded
      if (customersLoadedRef.current) {
        return;
      }
      
      try {
        setLoading(true);
        const response = await api.get('/customers');
        
        let customerList = response.data;
        
        // Add Walk In Customer for B2C type
        const walkInCustomer = { _id: 'walk-in', name: 'Walk In Customer', customerType: 'B2C', vatNumber: 'N/A', email: 'N/A' };
        customerList = [
          walkInCustomer,
          ...customerList
        ];
        
        setCustomers(customerList);
        customersLoadedRef.current = true;
        
        // Set Walk In Customer as the default selected customer if no customer is selected
        if (!invoice.customer) {
          setInvoice(prev => ({
            ...prev,
            customer: walkInCustomer
          }));
        }
        
        // If we have draft data already fetched and customers are loaded, we can load it into the form
        if (fromDraft && products.length > 0 && !draftDataLoadedRef.current && draftDataRef.current.draftInvoice) {
          loadDraftInvoiceData();
        }
      } catch (error) {
        console.error('Error loading customers:', error);
        enqueueSnackbar('Error loading customers', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
    
    // Empty dependency array means this only runs once when component mounts
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Add useEffect to fetch agents
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        setLoading(true);
        
        // Use the API service which automatically includes the auth token
        const response = await api.get('/masters/agents');
        
        if (response.data && Array.isArray(response.data)) {
          setAgents(response.data);
        } else {
          setAgents([]);
        }
      } catch (error) {
        console.error('Error fetching agents:', error);
        enqueueSnackbar('Error loading agents: ' + (error.response?.data?.message || error.message), { variant: 'error' });
        setAgents([]);
      } finally {
        setLoading(false);
      }
    };

      fetchAgents();
  }, [enqueueSnackbar]);

  // Handle quotation to invoice conversion
  useEffect(() => {
    if (location.state?.fromQuotation && location.state?.invoiceData && customers.length > 0 && products.length > 0) {
      const { invoiceData, quotationNo } = location.state;
      const { master, details } = invoiceData;
      
      // Find the customer
      const customer = customers.find(c => c.code === master.customerCode || c.name === master.customerName);
      
      // Transform details to invoice items format
      const transformedItems = details.map(detail => {
        const product = products.find(p => p.id === detail.itemId);
        return {
          id: Date.now() + Math.random(),
          product: product || {
            id: detail.itemId,
            productCode: detail.itemCode,
            name: detail.description,
            price: detail.unitPrice,
            vat: detail.vat
          },
          description: detail.description,
          quantity: detail.qty,
          price: detail.unitPrice,
          vatRate: detail.vat,
          discount: detail.discountPerc,
          discountAmount: detail.discountAmt,
          unit: detail.unit || '',
          lineTotal: detail.qty * detail.unitPrice,
          afterDiscount: detail.netValue,
          vatAmount: detail.vatAmt,
          total: detail.grandTotal,
          priceWithVat: detail.unitPrice * (1 + detail.vat / 100)
        };
      });
      
      // Set customer type
      setCustomerType(master.customerType);
      
      // Update invoice state
      setInvoice(prev => ({
        ...prev,
        customer: customer || null,
        items: transformedItems,
        notes: master.notes || prev.notes,
        subtotal: master.totalAmt.subtotal,
        vat: master.totalAmt.vat,
        additionalCost: master.totalAmt.additionalCost || 0,
        discountAmount: master.totalAmt.discountAmount || 0,
        total: master.totalAmt.subtotal + master.totalAmt.vat + (master.totalAmt.additionalCost || 0) - (master.totalAmt.discountAmount || 0),
        quotationReference: quotationNo // Store quotation reference
      }));
      
      enqueueSnackbar(`Invoice created from Quotation ${quotationNo}`, { variant: 'success' });
      
      // Clear the location state to prevent re-loading on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state, customers, products, enqueueSnackbar]);

  const handleCustomerChange = (event, newValue) => {
    setInvoice({
      ...invoice,
      customer: newValue,
    });
  };

  const handleDateChange = (newDate) => {
    setInvoice({
      ...invoice,
      date: newDate,
    });
  };

  const handleDueDateChange = (newDate) => {
    setInvoice({
      ...invoice,
      dueDate: newDate,
    });
  };

  const handleNewItemChange = (field, value) => {
    // Update newItem state
    const updatedNewItem = {
      ...newItem,
      [field]: value
    };
    
    // If this is a unit change, update the price accordingly
    if (field === 'unit' && newItem.product) {
      if (value === newItem.product.basicUnit) {
        updatedNewItem.price = newItem.product.price;
      } else if (value === newItem.product.alternateUnit) {
        updatedNewItem.price = newItem.product.altSalesRate;
      }
    }
    
    // Calculate totals for the new item with bidirectional support
    const calculatedItem = calculateItemTotals(updatedNewItem, field);
    
    // Update newItem state with calculated values
    setNewItem(calculatedItem);
    
    // Create a temporary items array that includes the new item
    const tempItems = [...invoice.items, calculatedItem];
    
    // Calculate new totals for the entire invoice
    const totals = calculateTotal(tempItems);
    
    // Update invoice state with new totals
    setInvoice(prev => ({
      ...prev,
      ...totals
    }));
  };

  const handleAddItem = useCallback(() => {
    console.log('handleAddItem called with newItem:', newItem);
    if (!newItem.product || !newItem.product.id) {
      console.log('handleAddItem: No product or product ID, returning');
      return;
    }
    console.log('handleAddItem: Adding item to invoice');
    
    // Create the new item with all calculated values
    const itemToAdd = {
      id: Date.now(), // Use timestamp as unique ID
          product: newItem.product,
          description: newItem.description || newItem.product.description,
          quantity: newItem.quantity,
          price: newItem.price || newItem.product.price,
          vatRate: newItem.vatRate || newItem.product.vat,
          discount: newItem.discount,
      unit: newItem.unit || newItem.product.basicUnit,
      ...calculateItemTotals(newItem)
    };
    
    // Update invoice state with new item
    setInvoice(prev => {
      const updatedItems = [...prev.items, itemToAdd];
      
      // Calculate new totals
      const totals = calculateTotal(updatedItems);
      
      return {
        ...prev,
        items: updatedItems,
        ...totals
      };
    });

    // Reset newItem state
    setNewItem({
      product: null,
      description: '',
      quantity: 1,
      price: 0,
      vatRate: 15,
      discount: 0,
      unit: ''
    });

    // Focus the product input after adding an item
    // Commented out to allow Qty field focus when auto-adding via Enter key
    // setTimeout(() => productInputRef.current?.focus(), 0);
  }, [newItem, calculateItemTotals]);

  // Auto-add item when product is selected via Enter key
  useEffect(() => {
    if (shouldAutoAddItem && newItem.product && newItem.product.id) {
      console.log('useEffect: Auto-adding item with product:', newItem.product.name);
      
      handleAddItem();
      setShouldAutoAddItem(false); // Reset flag
      
      // Focus on Qty field of the newly added row (the row that was just added, not the new empty row)
      setTimeout(() => {
        console.log('Attempting to focus on qty field of newly added row');
        // Find all quantity fields in existing invoice items (not the new item row)
        const allQtyFields = document.querySelectorAll('[data-item-id][data-field="quantity"]');
        console.log('Total quantity fields found:', allQtyFields.length);
        
        // Get the last quantity field which belongs to the newly added row
        if (allQtyFields.length > 0) {
          const lastQtyField = allQtyFields[allQtyFields.length - 1];
          console.log('Focusing on qty field of the last added item (not new empty row)');
          lastQtyField.focus();
          // No .select() to prevent text selection
        } else {
          console.log('No quantity fields found to focus');
        }
      }, 150);
    }
  }, [shouldAutoAddItem, newItem.product, handleAddItem, invoice.items.length]);

  const handleRemoveItem = (itemId) => {
    setInvoice({
      ...invoice,
      items: invoice.items.filter(item => item.id !== itemId),
    });
  };

  const handleProductChange = (event, newValue, shouldAutoAdd = false) => {
    console.log('handleProductChange called:', { product: newValue?.name, shouldAutoAdd });
    if (newValue) {
      // Create the item with product data
      const itemWithProduct = {
        product: newValue,
        description: newValue.description || '',
        quantity: 1,
        price: newValue.price || 0,
        vatRate: newValue.vat || 15,
        discount: 0,
        unit: newValue.basicUnit || '',
      };
      
      // Apply calculations to get all the computed fields
      const calculatedItem = calculateItemTotals(itemWithProduct);
      
      // Set the new item with all calculated values
      setNewItem({
        ...calculatedItem,
        product: newValue  // Ensure product reference is maintained
      });

      // If auto-add is requested (Enter key pressed), set flag for useEffect to handle
      if (shouldAutoAdd) {
        console.log('Auto-add requested, setting flag for useEffect');
        setShouldAutoAddItem(true);
      }
    } else {
      // If no product is selected, reset to base item
      setNewItem({
        product: null,
        description: '',
        quantity: 1,
        price: 0,
        vatRate: 15,
        discount: 0,
        unit: '',
      });
    }
  };

  const handleItemChange = (itemId, field, value) => {
    const updatedItems = invoice.items.map(item => {
      if (item.id === itemId) {
        let updatedItem = { ...item };
        
        // Handle unit change
        if (field === 'unit') {
          if (value === item.product.basicUnit) {
            updatedItem.price = item.product.price;
          } else if (value === item.product.alternateUnit) {
            updatedItem.price = item.product.altSalesRate;
          }
        }
        
        // Update the field
        updatedItem[field] = value;
        
        // Calculate all dependent values with bidirectional support
        const calculatedItem = calculateItemTotals(updatedItem, field);
        
        return calculatedItem;
      }
      return item;
    });

    // Update invoice state with new items and recalculate totals
    setInvoice(prev => {
      const totals = calculateTotal(updatedItems);
      
      return {
        ...prev,
        items: updatedItems,
        ...totals
      };
    });
  };

  const handleKeyDown = (event, itemId, field, fieldIndex) => {
    if (event.key === 'Tab' || event.key === 'Enter') {
      if (event.key === 'Enter') {
        event.preventDefault();
      }
      
      const fields = ['quantity', 'price', 'discount', 'vatRate'];
      const visibleFields = fields.filter(f => {
        if (f === 'discount') return visibleColumns.discountPerc;
        return true;
      });
      
      const currentIndex = visibleFields.indexOf(field);
      
      if (currentIndex !== -1) {
        let nextItemId = itemId;
        let nextFieldIndex = currentIndex + 1;
        
        // If we're at the last field, move to the next item
        if (nextFieldIndex >= visibleFields.length) {
          nextFieldIndex = 0;
          // Find the next item index
          const itemIndex = invoice.items.findIndex(item => item.id === itemId);
          if (itemIndex < invoice.items.length - 1) {
            nextItemId = invoice.items[itemIndex + 1].id;
          } else if (event.key === 'Enter') {
            // If on last field of last item, focus on the add new item field
            const addNewItemField = document.querySelector(`[data-new-item-field="${visibleFields[0]}"]`);
            if (addNewItemField) {
              addNewItemField.focus();
              addNewItemField.select();
              return;
            }
          }
        }
        
        // Focus the next field
        const nextField = document.querySelector(`[data-item-id="${nextItemId}"][data-field="${visibleFields[nextFieldIndex]}"]`);
        if (nextField) {
          nextField.focus();
          nextField.select();
        }
      }
    }
  };
  
  const handleNewItemKeyDown = (event, field) => {
    console.log('handleNewItemKeyDown called:', { 
      key: event.key, 
      field, 
      hasProduct: !!newItem.product,
      productName: newItem.product?.name 
    });
    
    if (event.key === 'Tab' || event.key === 'Enter') {
      if (event.key === 'Enter') {
        console.log('Enter key detected on field:', field);
        event.preventDefault();
        
        // If Enter is pressed on the product field and a product is selected, add the item immediately
        if (field === 'product' && newItem.product) {
          console.log('Auto-adding item from product field:', newItem.product.name);
          handleAddItem();
          // Focus back on the product field for next entry
          // Commented out to keep focus on Qty field of newly added row
          // setTimeout(() => {
          //   if (productInputRef.current) {
          //     console.log('Focusing back on product field');
          //     productInputRef.current.focus();
          //   } else {
          //     console.log('productInputRef.current is null');
          //   }
          // }, 100);
          return;
        } else if (field === 'product') {
          console.log('Product field but no product selected');
        }
        
        // If Enter is pressed on the last field and we have a product, add the item
        const fields = ['quantity', 'price', 'discount', 'vatRate'];
        const visibleFields = fields.filter(f => {
          if (f === 'discount') return visibleColumns.discountPerc;
          return true;
        });
        
        if (field === visibleFields[visibleFields.length - 1] && newItem.product) {
          handleAddItem();
          // Focus back on the product field
          const productField = document.querySelector('[data-new-item-field="product"]');
          if (productField) {
            setTimeout(() => {
              productField.focus();
            }, 0);
          }
          return;
        }
      }
      
      const fields = ['product', 'quantity', 'price', 'discount', 'vatRate'];
      const visibleFields = fields.filter(f => {
        if (f === 'discount') return visibleColumns.discountPerc;
        return true;
      });
      
      const currentIndex = visibleFields.indexOf(field);
      
      if (currentIndex !== -1 && currentIndex < visibleFields.length - 1) {
        // Focus the next field
        const nextField = document.querySelector(`[data-new-item-field="${visibleFields[currentIndex + 1]}"]`);
        if (nextField) {
          nextField.focus();
          nextField.select();
        }
      }
    }
  };

  const calculateSubtotal = (items) => {
    return items.reduce((sum, item) => {
      const itemTotal = (item.quantity || 0) * (item.price || 0);
      return sum + itemTotal;
    }, 0);
  };

  const calculateVat = (items) => {
    // Calculate subtotal first
    const subtotal = items.reduce((sum, item) => {
      const itemTotal = (item.quantity || 0) * (item.price || 0);
      return sum + itemTotal;
    }, 0);
    
    // Get discount amount
    const discountAmount = parseFloat(invoice.discountAmount) || 0;
    
    // Calculate taxable amount (subtotal - discount)
    const taxableAmount = subtotal - discountAmount;
    
    // Calculate VAT on taxable amount
    // Assuming items have proportional VAT rates, we need to calculate VAT per item based on taxable amount
    const totalItemsValue = subtotal;
    
    return items.reduce((sum, item) => {
      const itemTotal = (item.quantity || 0) * (item.price || 0);
      // Calculate this item's proportion of the total
      const itemProportion = totalItemsValue > 0 ? itemTotal / totalItemsValue : 0;
      // Apply discount proportionally to this item
      const itemTaxableAmount = itemTotal - (discountAmount * itemProportion);
      // Calculate VAT on the taxable amount for this item
      const itemVat = (itemTaxableAmount * (item.vatRate || 0)) / 100;
      return sum + itemVat;
    }, 0);
  };

  const calculateTotal = (items) => {
    const subtotal = calculateSubtotal(items);
    const discountAmount = parseFloat(invoice.discountAmount) || 0;
    const taxableAmount = subtotal - discountAmount;
    const vat = calculateVat(items);
    const additionalCost = parseFloat(invoice.additionalCost) || 0;
    const total = taxableAmount + vat + additionalCost;
    
    return {
      subtotal,
      vat,
      additionalCost,
      discountAmount,
      taxableAmount,
      total
    };
  };

  const saveInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Update payment status
      let updatedInvoice = { ...invoice };
    if (getTotalPayments() >= calculateTotal(invoice.items).total) {
      updatedInvoice.paymentStatus = 'Paid';
    } else if (getTotalPayments() > 0) {
      updatedInvoice.paymentStatus = 'Pending';
    } else {
      updatedInvoice.paymentStatus = 'Pending';
    }
    
      // Save to backend
      const response = await api.post('/invoices', updatedInvoice);
      
      setInvoice(response.data);
      enqueueSnackbar('Invoice saved successfully!', { variant: 'success' });
    } catch (err) {
      setError(err.message || 'Failed to save invoice');
      enqueueSnackbar(err.message || 'Failed to save invoice', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };
  
  // Save invoice as draft (Hold)
  const saveDraftInvoice = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare master data for draft invoice
      const customerTypeValue = customerType || 'B2C';
      
      // Create master data with safe access to customer data
      const masterData = prepareMasterData(invoice, customerTypeValue);
      
      // Prepare details data
      const detailsData = invoice.items.map(item => {
        const itemData = {
          itemId: item.id || item._id,
          itemCode: item.product?.productCode || '',
          description: item.product?.name || item.description || '',
          qty: Number(item.quantity) || 0,
          unitPrice: Number(item.price) || 0,
          discountPerc: Number(item.discount) || 0,
          discountAmt: (Number(item.price) * Number(item.quantity) * Number(item.discount) / 100) || 0,
          vat: Number(item.vatRate) || 15, // Default VAT rate
          vatAmt: Number(item.vatAmount) || 0,
          grandTotal: Number(item.total) || 0
        };
        return itemData;
      });
      
      // Prepare payments data if any
      const paymentsData = invoice.payments.map(payment => ({
        amount: Number(payment.amount) || 0,
        method: payment.method || 'cash',
        reference: payment.reference || '',
        date: payment.date || new Date()
      }));
      
      // Prepare request body
      const requestBody = {
        master: masterData,
        details: detailsData,
        payments: paymentsData
      };
      
      // Call the draft invoice API
      const response = await draftInvoiceService.createDraftInvoice(requestBody);
      
      enqueueSnackbar('Invoice saved as draft successfully!', { variant: 'success' });
      
      // Show success animation
      setLoading(false);
      setShowSuccessAnimation(true);
      
      // Navigate back to sales page after animation completes
      setTimeout(() => {
        navigate('/sales');
      }, 2500);
    } catch (err) {
      console.error('Error saving draft invoice:', err);
      setError(err.message || 'Failed to save draft invoice');
      enqueueSnackbar(err.message || 'Failed to save draft invoice', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  

  const handleCustomerTypeChange = (event) => {
    const newType = event.target.value;
    setCustomerType(newType);
    // Reset customer selection when changing type
      setInvoice({
        ...invoice,
        customer: null
      });
  };

  const handleAddPayment = (paymentData) => {
    if (!paymentData) {
      // Legacy direct call from the old code
    if (!invoice.paymentAmount || parseFloat(invoice.paymentAmount) <= 0) {
      alert('Please enter a valid payment amount');
      return;
    }

    const newPayment = {
      id: Date.now(),
        date: new Date(),
      method: invoice.paymentMethod,
        methodName: invoice.paymentMethod, // Add method name
      amount: parseFloat(invoice.paymentAmount),
        reference: invoice.paymentReference || '',
        category: 'unknown'
    };

    // Calculate new payment status
    const newTotalPaid = getTotalPayments() + parseFloat(invoice.paymentAmount);
    const totalAmount = calculateTotal(invoice.items).total;
    
    let newPaymentStatus = 'Pending';
    if (newTotalPaid >= totalAmount) {
      newPaymentStatus = 'Paid';
    } else if (newTotalPaid > 0) {
      newPaymentStatus = 'Partially Paid';
    }

    setInvoice({
      ...invoice,
      payments: [...invoice.payments, newPayment],
      paymentAmount: '',
      paymentReference: '',
      paymentStatus: newPaymentStatus
    });
    } else {
      // New call from PaymentSection component with proper date handling
      let paymentDate;
      
      try {
        if (paymentData.date instanceof Date) {
          paymentDate = paymentData.date;
        } else if (typeof paymentData.date === 'string' && paymentData.date) {
          paymentDate = new Date(paymentData.date);
        } else {
          paymentDate = new Date();
        }
      } catch (e) {
        paymentDate = new Date();
      }
                         
      const newPayment = {
        id: Date.now(),
        date: paymentDate,
        method: paymentData.method || 'Unknown',
        methodId: paymentData.methodId,
        methodName: paymentData.methodName || paymentData.method || 'Unknown', // Add method name
        amount: parseFloat(paymentData.amount) || 0,
        reference: paymentData.reference || '',
        category: paymentData.category || 'unknown'
      };

      // Calculate new payment status
      const newTotalPaid = getTotalPayments() + (parseFloat(paymentData.amount) || 0);
      const totalAmount = calculateTotal(invoice.items).total;
      
      let newPaymentStatus = 'Pending';
      if (newTotalPaid >= totalAmount) {
        newPaymentStatus = 'Paid';
      } else if (newTotalPaid > 0) {
        newPaymentStatus = 'Partially Paid';
      }

      setInvoice({
        ...invoice,
        payments: [...invoice.payments, newPayment],
        paymentAmount: '',
        paymentReference: '',
        paymentStatus: newPaymentStatus
      });
    }
  };

  const handleRemovePayment = (paymentId) => {
    const paymentToRemove = invoice.payments.find(p => p.id === paymentId);
    const newTotalPaid = getTotalPayments() - (paymentToRemove?.amount || 0);
    const totalAmount = calculateTotal(invoice.items).total;
    
    let newPaymentStatus = 'Pending';
    if (newTotalPaid >= totalAmount) {
      newPaymentStatus = 'Paid';
    } else if (newTotalPaid > 0) {
      newPaymentStatus = 'Partially Paid';
    }
    
    setInvoice({
      ...invoice,
      payments: invoice.payments.filter(payment => payment.id !== paymentId),
      paymentStatus: newPaymentStatus
    });
  };

  const getTotalPayments = () => {
    return invoice.payments.reduce((sum, payment) => sum + payment.amount, 0);
  };

  const getRemainingBalance = () => {
    const totalInvoice = calculateTotal(invoice.items);
    const totalPaid = getTotalPayments();
    return Math.max(0, totalInvoice.total - totalPaid);
  };

  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleColumnVisibilityChange = (columnName) => {
    setVisibleColumns(prev => ({
      ...prev,
      [columnName]: !prev[columnName]
    }));
  };

  const handleToggleTerms = () => {
    setTermsExpanded(!termsExpanded);
  };

  const handleAgentChange = (event) => {
    const agentId = event.target.value;
    if (agentId === '') {
      setInvoice({
        ...invoice,
        agent: null
      });
    } else {
      const selectedAgent = agents.find(agent => agent._id === agentId);
      setInvoice({
        ...invoice,
        agent: selectedAgent
      });
    }
  };

  const handleOpenAgentDialog = (mode, agent = null) => {
    setAgentDialogMode(mode);
    if (mode === 'edit' && agent) {
      setCurrentAgent(agent);
      setNewAgent(agent);
    } else {
      setCurrentAgent(null);
      setNewAgent({ name: '' });
    }
    setOpenAgentDialog(true);
  };

  // Add these state declarations at the beginning of the component
  const [validationErrors, setValidationErrors] = useState([]);
  const [validationDialogOpen, setValidationDialogOpen] = useState(false);

  return (
    <Container maxWidth={false} sx={{ mt: 0.5, px: { xs: 0.5, sm: 1, md: 1, lg: 1 } }}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <ValidationErrorDialog
        open={showValidationDialog}
        message={validationError}
        onClose={() => {
          setShowValidationDialog(false);
          setValidationError(null);
        }}
      />
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <IconButton 
              onClick={() => navigate('/sales')}
              sx={{ mr: 0.5, bgcolor: 'rgba(67, 97, 238, 0.1)' }}
            >
              <CloseIcon />
            </IconButton>
            <Box>
              <Typography variant="h5" component="h1" fontWeight="bold" sx={{ fontSize: '1.25rem' }}>
                New Sales Invoice
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25, fontSize: '0.875rem' }}>
                Create a new sales invoice
              </Typography>
            </Box>
          </Box>
          <Box>
            <Button 
              variant="outlined" 
              onClick={() => navigate('/sales')}
              sx={{ mr: 0.5, fontSize: '0.875rem' }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained" 
              color="error"
              startIcon={<PauseIcon />}
              onClick={saveDraftInvoice}
              disabled={invoice.items.length === 0}
              sx={{ fontSize: '0.875rem' }}
            >
              Hold
            </Button>
          </Box>
        </Box>

        {/* Info Alert */}
        <Alert severity="info" sx={{ mb: 0.5, fontSize: '0.875rem' }}>
          This invoice will be submitted to ZATCA upon completion. All fields marked with * are required.
        </Alert>

        {/* Main Form Sections */}
        <Grid container spacing={0.5}>
          {/* Invoice Details Section */}
          <Grid item xs={12} md={6}>
            <InvoiceDetailsSection
              invoiceNumber={invoice.invoiceNumber}
              date={invoice.date}
              dueDate={invoice.dueDate}
              status={invoice.status}
              paymentStatus={invoice.paymentStatus}
              readOnlyInvoiceNumber={true}
              readOnlyStatus={true}
              showPaymentStatus={false}
              quotationReference={invoice.quotationReference}
              onInvoiceNumberChange={(value) => setInvoice({ ...invoice, invoiceNumber: value })}
              onDateChange={handleDateChange}
              onDueDateChange={handleDueDateChange}
              onStatusChange={(value) => setInvoice({ ...invoice, status: value })}
              onPaymentStatusChange={(value) => setInvoice({ ...invoice, paymentStatus: value })}
            />
          </Grid>

          {/* Customer Section */}
          <Grid item xs={12} md={6}>
            <CustomerInfoSection
              customerType={customerType}
              customer={invoice.customer}
              agent={invoice.agent}
              customers={customers}
              agents={agents}
              onCustomerTypeChange={handleCustomerTypeChange}
              onCustomerChange={handleCustomerChange}
              onAgentChange={handleAgentChange}
              onOpenAgentDialog={handleOpenAgentDialog}
            />
          </Grid>
        </Grid>

        {/* Invoice Items Section */}
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Invoice Items
        </Typography>

        <InvoiceItemsTable
          items={invoice.items}
          newItem={newItem}
          visibleColumns={visibleColumns}
          products={products}
          loading={loading || productsLoading} // Include products loading state
          onItemChange={handleItemChange}
          onRemoveItem={handleRemoveItem}
          onAddItem={handleAddItem}
          onProductChange={handleProductChange}
          onNewItemChange={handleNewItemChange} // Use the new function with calculations
          onKeyDown={handleKeyDown}
          onNewItemKeyDown={handleNewItemKeyDown}
          onFocus={handleFocus}
          onSettingsClick={handleSettingsClick}
          onColumnToggle={handleColumnVisibilityChange}
          productInputRef={productInputRef}
        />

        {/* Payment Information and Invoice Summary Section */}
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <PaymentSection
                payments={invoice.payments}
                paymentMethod={invoice.paymentMethod}
                paymentAmount={invoice.paymentAmount}
                paymentReference={invoice.paymentReference}
                grandTotal={calculateTotal(invoice.items).total}
                totalPaid={getTotalPayments()}
                remainingBalance={getRemainingBalance()}
                onPaymentMethodChange={(value) => setInvoice({ ...invoice, paymentMethod: value })}
                onPaymentAmountChange={(value) => setInvoice({ ...invoice, paymentAmount: value })}
                onPaymentReferenceChange={(value) => setInvoice({ ...invoice, paymentReference: value })}
                onAddPayment={handleAddPayment}
                onRemovePayment={handleRemovePayment}
                expanded={paymentExpanded}
                onExpandChange={(event, isExpanded) => setPaymentExpanded(isExpanded)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
                  Invoice Summary
                </Typography>
                <Paper 
                  elevation={0} 
                  sx={{ 
                    p: 1.5, 
                    borderRadius: '4px', 
                    bgcolor: '#ffffff', 
                    border: '1px solid rgba(0, 0, 0, 0.12)'
                  }}
                >
                  {/* Summary Section */}
                  <Box sx={{ mb: 1.5 }}>
                    {/* Subtotal */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1
                    }}>
                      <Typography variant="body1">Subtotal</Typography>
                      <Typography variant="body1" sx={{ mr: 1 }}>{calculateSubtotal(invoice.items).toFixed(2)}</Typography>
                    </Box>

                    {/* Additional Cost field removed as requested */}

                    {/* Discount - Only show if enabled in settings */}
                    {salesSettings.documentDiscountEnabled && (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        mb: 1
                      }}>
                        <Typography variant="body1">Discount</Typography>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TextField
                            value={invoice.discountPercentage === '0' ? '' : invoice.discountPercentage}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              const discountAmount = (calculateSubtotal(invoice.items) * value / 100).toFixed(2);
                              setInvoice({ 
                                ...invoice, 
                                discountPercentage: e.target.value,
                                discountAmount: discountAmount === '0.00' ? '' : discountAmount
                              });
                            }}
                            type="number"
                            size="small"
                            InputProps={{
                              inputProps: { 
                                min: 0, 
                                max: 100, 
                                step: 0.01,
                                style: { textAlign: 'right', padding: '6px 8px' }
                              },
                              endAdornment: <Typography sx={{ mr: 0.5 }}>%</Typography>
                            }}
                            sx={{ 
                              width: '80px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                height: '32px'
                              },
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                display: 'none',
                                margin: 0
                              },
                              '& input[type=number]': {
                                MozAppearance: 'textfield'
                              }
                            }}
                          />
                          <Typography>=</Typography>
                          <TextField
                            value={invoice.discountAmount === '0' ? '' : invoice.discountAmount}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value) || 0;
                              const subtotal = calculateSubtotal(invoice.items);
                              const percentage = subtotal > 0 ? ((value / subtotal) * 100).toFixed(2) : 0;
                              setInvoice({ 
                                ...invoice, 
                                discountAmount: e.target.value,
                                discountPercentage: percentage === 0 ? '' : percentage
                              });
                            }}
                            type="number"
                            size="small"
                            InputProps={{
                              inputProps: { 
                                min: 0, 
                                step: 0.01,
                                style: { textAlign: 'right', padding: '6px 8px' }
                              }
                            }}
                            sx={{ 
                              width: '100px',
                              '& .MuiOutlinedInput-root': {
                                borderRadius: 0,
                                height: '32px'
                              },
                              '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': {
                                display: 'none',
                                margin: 0
                            },
                            '& input[type=number]': {
                              MozAppearance: 'textfield'
                            }
                          }}
                        />
                      </Box>
                    </Box>
                    )}

                    {/* Taxable Amount - Shows Subtotal minus Discount */}
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                      bgcolor: '#f8faff',
                      p: 1,
                      borderRadius: 1
                    }}>
                      <Typography variant="body1" fontWeight={600}>Taxable Amount</Typography>
                      <Typography variant="body1" fontWeight={600} sx={{ mr: 1 }}>
                        {(calculateSubtotal(invoice.items) - (parseFloat(invoice.discountAmount) || 0)).toFixed(2)}
                      </Typography>
                    </Box>

                    {/* VAT */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1
                    }}>
                      <Typography variant="body1">VAT</Typography>
                      <Typography variant="body1" sx={{ mr: 1 }}>{calculateVat(invoice.items).toFixed(2)}</Typography>
                    </Box>

                    <Divider sx={{ my: 1.5 }} />

                    {/* Total */}
                    <Box sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: getTotalPayments() > 0 ? 1.5 : 0
                    }}>
                      <Typography variant="h6" fontWeight={600}>Total</Typography>
                      <Typography variant="h6" fontWeight={600} sx={{ mr: 1 }}>{calculateTotal(invoice.items).total.toFixed(2)} SAR</Typography>
                    </Box>

                    {/* Payment Information */}
                    {getTotalPayments() > 0 && (
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: getRemainingBalance() > 0 ? '#fff4f4' : '#f0fff0',
                        p: 1,
                        borderRadius: 1,
                        mt: 1.5
                      }}>
                        <Typography variant="body1" fontWeight={600}>
                          {getRemainingBalance() > 0 ? 'Balance Due:' : 'Paid in Full'}
                        </Typography>
                        <Typography 
                          variant="body1" 
                          fontWeight={600} 
                          color={getRemainingBalance() > 0 ? "error.main" : "success.main"}
                          sx={{ mr: 1 }}
                        >
                          {getRemainingBalance().toFixed(2)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Action Buttons */}
                  <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between',
                    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                    pt: 1.5
                  }}>
                    <Button
                      variant="outlined"
                      startIcon={<PrintIcon />}
                      size="small"
                      sx={{ borderRadius: 0 }}
                      disabled={loading}
                    >
                      Print
                    </Button>
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
                      onClick={submitInvoice}
                      disabled={loading}
                      size="small"
                      sx={{ borderRadius: 0 }}
                    >
                      {loading ? 'Saving...' : 'Save'}
                    </Button>
                  </Box>
                </Paper>
              </Box>
            </Grid>
          </Grid>

          {/* Invoice Notes */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1.5 }}>
                  Notes
                </Typography>
                <TextField
                  multiline
                  rows={4}
                  value={invoice.notes}
                  onChange={(e) => setInvoice({ ...invoice, notes: e.target.value })}
                  fullWidth
                  placeholder="Add any notes for this invoice"
                  variant="outlined" 
                  size="small"
                  sx={{ 
                    ...multilineStyle,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 0,
                      width: '100%'
                    },
                  }}
                />
              </Box>
            </Grid>
          </Grid>
        </Box>

        {/* ZATCA QR Code Section */}
        <Box sx={{ mt: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Paper 
                elevation={0} 
                sx={{ 
                  p: 2, 
                  borderRadius: 0,
                  border: '1px solid rgba(0, 0, 0, 0.12)',
                  boxShadow: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2
                }}
              >
                {/* QR Code Section */}
                <Box sx={{ 
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 3
                }}>
                  <Box sx={{ 
                    p: 2, 
                    border: '1px solid rgba(0, 0, 0, 0.12)',
                    borderRadius: 0,
                    bgcolor: '#ffffff',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    minWidth: 200
                  }}>
                    <Typography variant="subtitle1" fontWeight={600} sx={{ fontSize: '0.875rem', alignSelf: 'flex-start' }}>
                      ZATCA QR Code
                    </Typography>
                    <Box 
                      component="img" 
                      src="https://api.qrserver.com/v1/create-qr-code/?size=150x150&data=ZATCA-DEMO-INVOICE-123456" 
                      alt="ZATCA QR Code"
                      sx={{ 
                        width: 150, 
                        height: 150,
                        objectFit: 'contain',
                        filter: 'blur(4px)',
                        opacity: 0.7,
                        transition: 'filter 0.3s, opacity 0.3s'
                      }}
                    />
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Box sx={{ 
                      textAlign: 'right',
                      direction: 'rtl',
                      fontFamily: 'Arial, sans-serif',
                      mb: 2
                    }}>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem', mb: 1, fontWeight: 500 }}>
                        فاتورة إلكترونية معتمدة من هيئة الزكاة والضريبة والجمارك
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                        رمز الاستجابة السريعة (QR) يحتوي على تفاصيل الفاتورة الضريبية المبسطة
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        يمكنك مسح رمز الاستجابة السريعة للتحقق من صحة الفاتورة وتفاصيلها
                      </Typography>
                    </Box>

                    <Box sx={{ 
                      textAlign: 'left',
                      borderTop: '1px solid rgba(0, 0, 0, 0.12)',
                      pt: 2
                    }}>
                      <Typography variant="body1" sx={{ fontSize: '0.875rem', mb: 1, fontWeight: 500 }}>
                        ZATCA E-Invoice Compliant
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', mb: 1 }}>
                        The QR code contains the details of the simplified tax invoice or standard tax invoice
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                        Please Scan the QR code to verify the invoice details and authenticity
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Box>

        {/* Column Visibility Popover */}
        <Popover
          id="column-visibility-menu"
          open={settingsOpen}
          anchorEl={settingsAnchorEl}
          onClose={handleSettingsClose}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
        >
          <Box sx={{ p: 2, width: 250 }}>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Column Visibility
            </Typography>
            <List dense>
              <ListItem button onClick={() => handleColumnVisibilityChange('description')}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.description}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary="Description" />
              </ListItem>
              <ListItem button onClick={() => handleColumnVisibilityChange('priceWithVat')}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.priceWithVat}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary="Price + VAT" />
              </ListItem>
              <ListItem button onClick={() => handleColumnVisibilityChange('discountPerc')}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.discountPerc}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary="Discount %" />
              </ListItem>
              <ListItem button onClick={() => handleColumnVisibilityChange('discountAmt')}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.discountAmt}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary="Discount Amount" />
              </ListItem>
              <ListItem button onClick={() => handleColumnVisibilityChange('netValue')}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.netValue}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary="Net Value" />
              </ListItem>
              <ListItem button onClick={() => handleColumnVisibilityChange('vatAmount')}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.vatAmount}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary="VAT Amount" />
              </ListItem>
              <ListItem button onClick={() => handleColumnVisibilityChange('grandTotal')}>
                <ListItemIcon>
                  <Checkbox
                    edge="start"
                    checked={visibleColumns.grandTotal}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemText primary="Grand Total" />
              </ListItem>
            </List>
          </Box>
        </Popover>
      </Paper>

      {/* Loading Overlay */}
      <LoadingOverlay open={loading} />

      {/* Success Animation */}
      <SuccessAnimation
        open={showSuccessAnimation}
        onClose={() => setShowSuccessAnimation(false)}
        message="Success!"
      />
    </Container>
  );
}

export default SalesInvoice; 